// src/components/Pages/Settings.js

import { useMemo, useState } from 'react';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';

import {
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Container,
  Divider,
  Grid,
  LinearProgress,
  Typography,
} from '@mui/material';

import ButtonAction from '../Controls/Buttons/ButtonAction';
import ButtonLoading from '../Controls/Buttons/ButtonLoading';
import SelectBasic from '../Controls/FormsUI/SelectBasic';
import ResetUserPass from '../Pages/Authentication/ResetUserPass';
import { initialValuesSettings } from '../Apm/Tables/InitialValues/initialValuesSettings';
import { useRetrieveUserQuery, useUpdateCurrentUserMutation } from '../../redux/features/authApi';

const validationSchema = Yup.object({
  lang: Yup.mixed().nullable(),
});

const getErrorStatus = (error) => {
  return error?.status ?? error?.originalStatus ?? error?.response?.status ?? null;
};

const getLanguageCode = (value) => {
  if (!value) {
    return null;
  }

  if (typeof value === 'string') {
    return value;
  }

  if (typeof value === 'object') {
    return value.value ?? value.code ?? value.lang ?? null;
  }

  return null;
};

export default function Settings() {
  const { t, i18n } = useTranslation();

  const [edit, setEdit] = useState(false);

  const {
    data: account,
    isLoading: isAccountLoading,
    isFetching: isAccountFetching,
    isError: isAccountError,
  } = useRetrieveUserQuery();

  const [updateCurrentUser, { isLoading: isUpdating }] = useUpdateCurrentUserMutation();

  const formInitialValues = useMemo(
    () => ({
      ...initialValuesSettings,
      ...(account ?? {}),
    }),
    [account]
  );

  const handleSubmit = async (values, { resetForm }) => {
    if (!account) {
      toast.error(() => t('toast.record_not_found'));

      return;
    }

    try {
      const updatedAccount = await updateCurrentUser({
        lang: values.lang ?? null,
      }).unwrap();

      resetForm({
        values: {
          ...initialValuesSettings,
          ...updatedAccount,
        },
      });

      setEdit(false);

      /*
       * Supports responses such as:
       *
       * lang: "ro"
       *
       * or:
       *
       * lang: {
       *   value: "ro",
       *   label: "Română",
       * }
       */
      const languageCode = getLanguageCode(updatedAccount?.lang) ?? getLanguageCode(values.lang);

      if (languageCode && languageCode !== i18n.resolvedLanguage) {
        await i18n.changeLanguage(languageCode);
      }

      toast.success(() => t('toast.settings_updated_successfully'));
    } catch (error) {
      console.error('Settings update failed:', error);

      console.error('Settings validation response:', error?.data);

      const status = getErrorStatus(error);

      if (status === 400) {
        toast.error(() => t('toast.not_saved_try_again'), {
          autoClose: 4000,
        });

        return;
      }

      if (status === 401 || status === 403) {
        toast.error(() => t('toast.not_authorized'), {
          autoClose: 4000,
        });

        return;
      }

      if (status === 404) {
        toast.error(() => t('toast.record_not_found'), {
          autoClose: 4000,
        });

        return;
      }

      toast.error(() => t('toast.not_saved_try_again'), {
        autoClose: 4000,
      });
    }
  };

  if (isAccountLoading && !account) {
    return (
      <Container maxWidth="md" disableGutters>
        <LinearProgress
          sx={{
            mt: 7,
          }}
        />
      </Container>
    );
  }

  if (isAccountError || !account) {
    return (
      <Container maxWidth="md" disableGutters>
        <Typography
          align="center"
          color="error"
          sx={{
            mt: 7,
          }}
        >
          {t('toast.record_not_found')}
        </Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" disableGutters>
      <Typography
        align="center"
        variant="h4"
        sx={{
          mt: 7,
          mb: 2,
        }}
      >
        {t('settings.settings')}
      </Typography>

      {isAccountFetching && <LinearProgress />}

      <Formik
        enableReinitialize={!edit}
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleSubmit}
      >
        {({ resetForm, dirty }) => {
          const handleCancel = () => {
            resetForm({
              values: formInitialValues,
            });

            setEdit(false);
          };

          return (
            <Form autoComplete="off" noValidate>
              <Card
                sx={{
                  mt: 3,
                }}
              >
                <CardHeader
                  title={
                    <Grid container item>
                      <Grid item xs={6}>
                        <Typography variant="h6">{t('settings.settings')}</Typography>
                      </Grid>

                      <Grid item xs={4} />

                      <Grid container item xs={2} justifyContent="flex-end">
                        <ButtonAction type="button" color="error" disabled={!edit || isUpdating} onClick={handleCancel}>
                          <Typography variant="button">{t('button.cancel')}</Typography>
                        </ButtonAction>
                      </Grid>
                    </Grid>
                  }
                  sx={{
                    backgroundColor: '#f1f1f1',
                  }}
                />

                <Divider />

                <CardContent>
                  <Grid container item spacing={2}>
                    <Grid
                      item
                      xs={12}
                      sx={{
                        width: '100%',
                      }}
                    >
                      <Typography
                        sx={{
                          mb: 1,
                        }}
                      >
                        {t('settings.choose_your_language')}
                      </Typography>

                      <Box
                        sx={{
                          display: 'flex',
                        }}
                      >
                        <Box
                          sx={{
                            flexGrow: 1,
                          }}
                        >
                          <SelectBasic name="lang" disabled={!edit || isUpdating} />
                        </Box>
                      </Box>
                    </Grid>
                  </Grid>
                </CardContent>

                <Divider />

                <CardActions
                  sx={{
                    mt: 3,
                    mb: 3,
                    ml: 1,
                    mr: 1,
                  }}
                >
                  <Grid container item>
                    <Grid container item xs={6}>
                      <ButtonLoading
                        type="button"
                        color="primary"
                        variant="outlined"
                        size="small"
                        disabled={edit || isUpdating}
                        onClick={() => setEdit(true)}
                      >
                        <Typography variant="button">{t('button.edit_settings')}</Typography>
                      </ButtonLoading>
                    </Grid>

                    <Grid container item xs={6} justifyContent="flex-end">
                      <ButtonLoading
                        type="submit"
                        color="primary"
                        variant="outlined"
                        loading={isUpdating}
                        size="small"
                        disabled={!edit || !dirty || isUpdating}
                      >
                        <Typography variant="button">{t('button.save')}</Typography>
                      </ButtonLoading>
                    </Grid>
                  </Grid>
                </CardActions>
              </Card>
            </Form>
          );
        }}
      </Formik>

      <Grid
        item
        xs={12}
        sx={{
          mt: 2,
        }}
      >
        <ResetUserPass />
      </Grid>
    </Container>
  );
}
